import * as R from "ramda";

import {
  adjustRangeValuesAtMostToCuttoff,
} from "./range";

export function replaceText(inlineJSON: InlineJsonContent, replaceRange: BaseRange, newText: string) {
  return adjustStyleRangesWithFunction({
    ...inlineJSON,
    text: inlineJSON.text.slice(0, replaceRange.start) + newText + inlineJSON.text.slice(replaceRange.end),
  }, (range) => adjustRangeValuesAtMostToCuttoff(range, newText.length - (replaceRange.end - replaceRange.start), replaceRange.start));
}

export function addStyle(inlineJSON: InlineJsonContent, style: Style, range: BaseRange) {
  return ({
    ...inlineJSON,
    styles: combineStyleRanges(inlineJSON.styles.concat([{
      ...style,
      range,
    }])),
  });
}

export function removeStyle(inlineJSON: InlineJsonContent, type: StyleType, range: BaseRange) {
  const newStyles = R.flatten(inlineJSON.styles.map((style) => {
    if (style.type !== type) { return style; }

    if (range.start <= style.range.start) {
      if (range.end >= style.range.end) {
        return [];
      } else if (range.end > style.range.start) {
        return {
          ...style,
          range: {
            start: range.end,
            end: style.range.end,
          },
        };
      } else {
        return style;
      }
    } else if (range.start < style.range.end) {
      if (range.end >= style.range.end) {
        return {
          ...style,
          range: {
            start: style.range.start,
            end: range.start,
          },
        };
      } else {
        return [{
          ...style,
          range: {
            start: style.range.start,
            end: range.start,
          },
        },{
          ...style,
          range: {
            start: range.end,
            end: style.range.end,
          },
        }];
      }
    } else {
      return style;
    }
  }));

  return ({
    ...inlineJSON,
    styles: newStyles,
  });
}

export function setStyleArg(inlineJSON: InlineJsonContent, type: StyleType, index: number, argKey: string, argValue: unknown) {
  const groupedStyles = R.groupBy((style) => style.type, inlineJSON.styles);

  const groupedStyle = groupedStyles[type];

  groupedStyles[type] = (groupedStyle || []).map((style) => {
    if (style.type === "a" && style.range.start <= index && style.range.end >= index) {
      return {
        ...style,
        args: {
          ...style.args,
          [argKey]: argValue,
        },
      };
    } else {
      return style;
    }
  });

  return ({
    ...inlineJSON,
    styles: R.flatten(Object.values(groupedStyles)),
  });
}

function adjustStyleRangesWithFunction(inlineJSON: InlineJsonContent, func: (range: BaseRange) => BaseRange) {
  return R.over(R.lensProp("styles"), R.map(R.over(R.lensProp("range"), func)), inlineJSON);
}


export function inlineJSONBeforeIndex(inlineJSON: InlineJsonContent, index: number) {
  return {
    text: inlineJSON.text.slice(0,index),
    styles: inlineJSON.styles.filter((style) => style.range.start < index).map((style) => {return {...style, range: {...style.range, end: Math.min(style.range.end, index)}};}),
  };
}

export function inlineJSONAfterIndex(inlineJSON: InlineJsonContent, index: number) {
  return {
    text: inlineJSON.text.slice(index),
    styles: inlineJSON.styles.filter((style) => style.range.end > index).map((style) => {return {...style, range: {...style.range, start: Math.max(style.range.start, index)}};}),
  };
}

function combineStyleRanges(styles: Array<StyleRange>) {
  const arrayOfGroupedStyles = R.values(R.groupBy((style) => style.type, styles));
  const arrayOfGroupedStylesCombined = arrayOfGroupedStyles.map((groupedStyle) => {
    if (!groupedStyle) { return []; }

    const sortedByStart = R.sortBy((style) => style.range.start, groupedStyle);

    return sortedByStart.reduce((acc: Array<StyleRange>, style) => {
      const lastStyle = R.last(acc);
      if (!lastStyle) {
        return [style];
      } else {
        if (lastStyle.range.end < style.range.start) {
          return acc.concat([style]);
        } else {
          return R.init(acc).concat([{
            ...lastStyle,
            range: {
              start: lastStyle.range.start,
              end: R.max(lastStyle.range.end, style.range.end),
            },
          }]);
        }
      }
    }, []);
  });

  return R.flatten(arrayOfGroupedStylesCombined);
}

export function splitInlineJSONAtIndex(inlineJSON: InlineJsonContent, index: number) {
  const [text1, text2] = R.splitAt(index, inlineJSON.text);
  const [styles1, styles2] = splitStylesAtIndex(inlineJSON.styles, index);

  return [
    {
      ...inlineJSON,
      text1: text1,
      styles: styles1,
    },
    {
      ...inlineJSON,
      text2: text2,
      styles: styles2,
    },
  ];
}

export function splitStylesAtIndex(styles: Array<StyleRange>, index: number) {
  const unadjustedStyles1 = styles.filter((style) => style.range.start < index);
  const unadjustedStyles2 = styles.filter((style) => style.range.end > index);

  const styles1 = unadjustedStyles1.map((style) => {
    return {
      ...style,
      range: {
        start: style.range.start,
        end: R.min(style.range.end, index),
      },
    };
  });

  const styles2 = unadjustedStyles2.map((style) => {
    return {
      ...style,
      range: {
        start: style.range.start - index,
        end: style.range.end - index,
      },
    };
  });

  return [styles1, styles2];
}
